/**
 * Per-OEM ComponentNames for the "service killed" remediation deep-link.
 * Sourced from dontkillmyapp.com. Native falls back to the app details
 * screen if the ComponentName no longer resolves. Policy: service-health.ts.
 */

export type OemKind = "samsung" | "xiaomi" | "oneplus" | "oppo" | "huawei" | "honor" | "vivo" | "stock";

export interface OemRemediation {
  kind: OemKind;
  intent: { component?: string; action?: string } | null;
  /** i18n key prefix. UI reads `<prefix>.title` and `<prefix>.step1..stepN`. */
  i18nPrefix: string;
}

export function detectOem(manufacturer: string): OemKind {
  const m = (manufacturer ?? "").toLowerCase().trim();
  if (m === "samsung") return "samsung";
  if (m === "xiaomi" || m === "redmi" || m === "poco") return "xiaomi";
  if (m === "oneplus") return "oneplus";
  if (m === "oppo" || m === "realme") return "oppo";
  if (m === "huawei") return "huawei";
  if (m === "honor") return "honor";
  if (m === "vivo" || m === "iqoo") return "vivo";
  return "stock";
}

const OEM_INTENTS: Record<OemKind, { component?: string; action?: string } | null> = {
  // Samsung Device Care > Battery (Sleeping apps).
  samsung: { component: "com.samsung.android.lool/.ui.battery.BatteryActivity" },
  // MIUI Autostart.
  xiaomi: { component: "com.miui.securitycenter/.permission.AutoStartManagementActivity" },
  // ColorOS: shared by OnePlus and Oppo.
  oneplus: { component: "com.coloros.safecenter/.permission.startup.StartupAppListActivity" },
  oppo: { component: "com.coloros.safecenter/.permission.startup.StartupAppListActivity" },
  // EMUI Phone Manager > Startup: shared by Huawei and Honor.
  huawei: { component: "com.huawei.systemmanager/.startupmgr.ui.StartupNormalAppListActivity" },
  honor: { component: "com.huawei.systemmanager/.startupmgr.ui.StartupNormalAppListActivity" },
  // Vivo iManager background control.
  vivo: { component: "com.iqoo.secure/.ui.phoneoptimize.BgStartUpManager" },
  stock: null,
};

export function remediationFor(manufacturer: string): OemRemediation {
  const kind = detectOem(manufacturer);
  return {
    kind,
    intent: OEM_INTENTS[kind],
    i18nPrefix: `msg.serviceKilled.oem.${kind}`,
  };
}
